import React, { useState } from 'react'
import {
  Plus,
  Edit,
  Trash2,
  User,
  Shield,
  CheckCircle,
  XCircle,
  X,
  Plug
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface UserData {
  id: string
  email: string
  first_name?: string
  last_name?: string
  role: 'admin' | 'analyst' | 'manager' | 'user' | 'viewer'
  is_active: boolean
  created_at: string
  last_login_at?: string
}

/**
 * One live (user, client) MCP connection.
 * Mirrors `McpConnection` in backend `modules/mcp/admin/dto/mcp-admin.dto.ts`.
 * `client_name` really can be null — a client row can be deleted while its
 * tokens outlive it.
 */
interface McpConnection {
  user_id: string
  client_id: string
  client_name: string | null
  live_token_count: number
  newest_token_created_at: string
  expires_at: string
}

interface CreateUserData {
  email: string
  password: string
  first_name?: string
  last_name?: string
  role: 'admin' | 'analyst' | 'manager' | 'user' | 'viewer'
}

export function UserManagement() {
  const { isAdmin } = useAuth()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [createUserData, setCreateUserData] = useState<CreateUserData>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'analyst',
  })

  const queryClient = useQueryClient()

  // Fetch all users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/auth/users')
      return response.data
    },
    enabled: isAdmin,
  })

  // Live MCP connections, keyed by user, for the MCP column.
  //
  // Deliberately fault-tolerant: the MCP module disables itself when its
  // database credential is missing, and this page must keep working when that
  // happens. An error here yields an empty map and the column reads "—" rather
  // than taking User Management down with it — managing people must never
  // depend on an optional integration being healthy.
  const { data: mcpConnections = [] } = useQuery<McpConnection[]>({
    queryKey: ['mcp-connections'],
    queryFn: async () => {
      try {
        const response = await api.get('/mcp/admin/connections')
        // Shape is { connections, total, distinctUsers } — not a bare array.
        return response.data?.connections ?? []
      } catch {
        return []
      }
    },
    enabled: isAdmin,
    retry: false,
    staleTime: 60_000,
  })

  const connectionsByUser = React.useMemo(() => {
    const map = new Map<string, McpConnection[]>()
    for (const c of mcpConnections) {
      const list = map.get(c.user_id)
      if (list) list.push(c)
      else map.set(c.user_id, [c])
    }
    return map
  }, [mcpConnections])

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData: CreateUserData) => {
      const response = await api.post('/auth/register', userData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreateForm(false)
      setCreateUserData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'analyst',
      })
      toast.success('User created successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create user')
    },
  })

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UserData> }) => {
      const response = await api.put(`/auth/users/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
      toast.success('User updated successfully')
    },
    onError: () => {
      toast.error('Failed to update user')
    },
  })

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/auth/users/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('User deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete user')
    },
  })

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    createUserMutation.mutate(createUserData)
  }

  const handleUpdateUser = (id: string, data: Partial<UserData>) => {
    updateUserMutation.mutate({ id, data })
  }

  const handleDeleteUser = (id: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(id)
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'badge-danger'
      case 'analyst': return 'badge-info'
      case 'manager': return 'badge-warning'
      case 'user': return 'badge-info'
      case 'viewer': return 'badge-gray'
      default: return 'badge-gray'
    }
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="h-16 w-16 mx-auto mb-4 text-secondary-400" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Access Denied</h3>
        <p className="text-secondary-500">You don't have permission to manage users.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900">User Management</h2>
          <p className="text-secondary-600">{users.length} users</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-secondary-900">Create New User</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={createUserData.email}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={createUserData.password}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={createUserData.first_name}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={createUserData.last_name}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>

              <div className="form-group md:col-span-2">
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={createUserData.role}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, role: e.target.value as any }))}
                >
                  <option value="analyst">Analyst (Read-only: view data, no exports, no edits)</option>
                  <option value="admin">Admin (Full access: all pages, exports, and edits)</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createUserMutation.isPending}
                className="btn-primary"
              >
                {createUserMutation.isPending ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  MCP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {users.map((user: UserData) => (
                <tr key={user.id} className="hover:bg-secondary-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                        <User className="h-4 w-4 text-primary-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-secondary-900">
                          {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.email
                          }
                        </div>
                        <div className="text-sm text-secondary-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`status-badge ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="status-badge badge-success">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="status-badge badge-danger">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const conns = connectionsByUser.get(user.id) ?? []
                      if (conns.length === 0) {
                        return <span className="text-sm text-secondary-400">—</span>
                      }
                      // client_name is nullable: a client row can be deleted
                      // while its tokens outlive it. Fall back to the id.
                      const clients = Array.from(
                        new Set(conns.map((c) => c.client_name || c.client_id)),
                      )
                      const since = new Date(conns[0].newest_token_created_at)
                      return (
                        <Link
                          to="/admin/mcp"
                          className="status-badge badge-success hover:opacity-80"
                          title={`${clients.join(', ')} — last token issued ${
                            Number.isNaN(since.valueOf()) ? 'unknown' : since.toLocaleDateString()
                          }. Manage in MCP Access.`}
                        >
                          <Plug className="h-3 w-3 mr-1" />
                          {conns.length > 1 ? `${conns.length} clients` : clients[0]}
                        </Link>
                      )
                    })()}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleDateString()
                      : 'Never'
                    }
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setEditingUser(user.id)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-danger-600 hover:text-danger-900"
                        disabled={deleteUserMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <User className="h-16 w-16 mx-auto mb-4 text-secondary-400" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No users found</h3>
            <p className="text-secondary-500 mb-4">Create your first user to get started.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary"
            >
              Add First User
            </button>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          userId={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleUpdateUser}
          isLoading={updateUserMutation.isPending}
        />
      )}
    </div>
  )
}

// Edit User Modal Component
function EditUserModal({
  userId,
  onClose,
  onSave,
  isLoading
}: {
  userId: string
  onClose: () => void
  onSave: (id: string, data: Partial<UserData>) => void
  isLoading: boolean
}) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    role: 'analyst' as UserData['role'],
    is_active: true,
  })

  const { data: user } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const response = await api.get(`/auth/users/${userId}`)
      return response.data
    },
  })

  React.useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role,
        is_active: user.is_active,
      })
    }
  }, [user])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(userId, formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-tagus-lg max-w-md w-full">
        <div className="p-6 border-b border-secondary-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-secondary-900">Edit User</h3>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input bg-secondary-50"
              value={user?.email || ''}
              disabled
            />
          </div>

          <div className="form-group">
            <label className="form-label">First Name</label>
            <input
              type="text"
              className="form-input"
              value={formData.first_name}
              onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Last Name</label>
            <input
              type="text"
              className="form-input"
              value={formData.last_name}
              onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <select
              className="form-input"
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
            >
              <option value="analyst">Analyst (Read-only: view data, no exports, no edits)</option>
              <option value="admin">Admin (Full access: all pages, exports, and edits)</option>
              {/* Legacy values preserved so existing accounts editing this user don't lose their role on submit */}
              {formData.role === 'manager' && (
                <option value="manager">Manager (legacy — convert to Admin or Analyst)</option>
              )}
              {formData.role === 'user' && (
                <option value="user">User (legacy — convert to Analyst)</option>
              )}
              {formData.role === 'viewer' && (
                <option value="viewer">Viewer (legacy — convert to Analyst)</option>
              )}
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="is_active" className="text-sm text-secondary-700">
              Active user
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
